import React, { ReactNode, useMemo, useState } from "react";
import {
  Button,
  useMediaQuery,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import GestureIcon from "@mui/icons-material/Gesture";
import styled from "@emotion/styled";
import {
  RangeSelectorControlProps,
  useRangeSelectControl,
  useChartColors,
} from "@hooks";
import { InvestmentChartData } from "@helpers/charts/mergeCloseByDate";
import { RangeSelector } from "@rentablo/components";
import { RecommendedItems } from "@features/investments/investment/types";
import { useMandatorVisibility } from "@helpers";
import { ChartLayout, Figure, ComparisonValue } from "../BaseChartTools";
import { getEventLabelForEvents } from "../highcharts/DrawLineChart/getEventLabel";
import { DrawLineChart } from "../highcharts";
import { prepareInvestmentsData } from "./utils";
import { InvestmentChartBottomBar } from "./InvestmentChartBottomBar";

const ChartActionsContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;

const DrawLinesButton = styled(Button)`
  padding: 3px 5px;
  width: max-content;
  height: max-content;
  border-radius: 8px;
`;

export interface InvestmentChartProps {
  title?: string | ReactNode;
  subtitle?: ReactNode;
  data: InvestmentChartData[];
  availableDateRanges?: IntervalType[];
  availableCompactDateRanges?: IntervalType[];
  className?: string;
  onSelect?: (type: IntervalType, value?: RangeType) => void;
  isFetching?: boolean;
  bottomBarComponent?: ReactNode;
  showEvents?: boolean;
  drawLines?: boolean;
  comparedTicker?: string | null;
  comparisonValues?: ComparisonValue[];
  onComparedTickerChange?: (ticker: string | null) => void;
  customRange?: RangeType;
  mainValueLabel?: string;
  recommendedItems?: RecommendedItems;
  hasRecommenderData?: {
    hasR1Data: boolean;
    hasR2Data: boolean;
    hasR3Data: boolean;
  };
  noDataMessage?: ReactNode;
  showNavigator?: boolean;
  showInCard?: boolean;
  chartHeight?: number;
  currency?: string;
  rangeProps?: RangeSelectorControlProps;
  hideControls?: boolean;
  hideRangeSelector?: boolean;
  figureAction?: ReactNode;
}

export const InvestmentChart = ({
  data,
  className,
  onSelect,
  isFetching,
  title,
  subtitle,
  bottomBarComponent,
  showEvents = false,
  drawLines = false,
  comparedTicker,
  recommendedItems,
  comparisonValues = [],
  availableDateRanges,
  availableCompactDateRanges,
  onComparedTickerChange,
  customRange,
  mainValueLabel,
  hasRecommenderData,
  noDataMessage,
  showNavigator = true,
  showInCard = false,
  chartHeight,
  currency,
  rangeProps,
  hideControls = false,
  hideRangeSelector = false,
  figureAction,
}: InvestmentChartProps) => {
  const theme = useTheme();
  const securitiesChartColors = useChartColors("securitiesChart");
  const isComparisonSelectorVisible = useMandatorVisibility({
    hideFor: ["kpmg-germany"],
  });
  const chartColors: Record<string, string> = useMemo(
    () => ({
      ...Object.fromEntries(
        Object.entries(securitiesChartColors).filter(
          ([, value]) => typeof value === "string"
        )
      ),
      recommender1: theme.palette.graphVisual["06"].mid,
      recommender2: theme.palette.graphVisual["07"].mid,
      recommender3: theme.palette.graphVisual["08"].mid,
    }),
    [securitiesChartColors, theme.palette.graphVisual]
  );
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const internalRangeProps = useRangeSelectControl({
    onSelect,
    firstDate: data[0] ? data[0].date : new Date(),
  });
  const { range, intervalType, customIntervalSelect, selectInterval } =
    rangeProps ?? internalRangeProps;

  const [lineDrawingEnabled, setLineDrawingEnabled] = useState(false);
  const [isChartHovering, setIsChartHovering] = useState(false);

  const currentTicker = comparisonValues
    .concat(comparisonValues)
    .find(({ value }) => value === comparedTicker);

  const isComparison =
    Boolean(comparedTicker) ||
    Object.values(recommendedItems ?? {}).some((item) => item);

  const markers = useMemo(
    () =>
      showEvents
        ? data
            .filter((x) => Boolean(x.events?.length))
            .map((x) => {
              const label = getEventLabelForEvents(x.events!, theme.palette);
              return {
                x: x.date.getTime(),
                title: label.name,
                style: {
                  color: label.color,
                },
                color: theme.palette.grey[400],
                data: [x.date.getTime(), x.eventsValue ?? 0] as [
                  number,
                  number,
                ],
                tooltipData: x.events,
              };
            })
        : [],
    [data, showEvents, theme.palette]
  );

  const chartData = useMemo(
    () =>
      prepareInvestmentsData(
        data,
        chartColors,
        isComparison,
        comparedTicker,
        hasRecommenderData,
        isFetching
      ),
    [
      data,
      chartColors,
      isComparison,
      comparedTicker,
      hasRecommenderData,
      isFetching,
    ]
  );

  return (
    <ChartLayout
      isFetching={isFetching}
      useCard={showInCard}
      className={className}
      intervalSelector={
        !customRange && !hideControls && !hideRangeSelector ? (
          <RangeSelector
            compact={isXs}
            range={intervalType === "custom" ? range : undefined}
            intervalType={intervalType}
            intervalTypes={availableDateRanges}
            compactIntervalTypes={availableCompactDateRanges}
            onSelect={selectInterval}
            onCustomSelect={customIntervalSelect}
          />
        ) : null
      }
      figureAction={figureAction}
      figure={
        title || subtitle ? (
          <Figure>
            {typeof title === "string" ? (
              <Typography variant={isXs ? "h4" : "h2"}>{title}</Typography>
            ) : (
              title
            )}
            {subtitle}
          </Figure>
        ) : null
      }
      bottomComponent={
        <InvestmentChartBottomBar
          disabled={intervalType === "oneDay"}
          comparisonValues={comparisonValues}
          selectedComparedValue={comparedTicker}
          onComparedValueChange={onComparedTickerChange}
          bottomBarComponent={bottomBarComponent}
          showComparisonSelector={isComparisonSelectorVisible && !hideControls}
        />
      }
      onChartMouseEnter={() => {
        setIsChartHovering(true);
      }}
      onChartMouseLeave={() => {
        setIsChartHovering(false);
      }}
    >
      <DrawLineChart
        allowDrawing={lineDrawingEnabled}
        chartData={chartData}
        chartType={isComparison ? "performance" : "valuation"}
        markers={markers}
        mainValueLabel={mainValueLabel}
        comparedTicker={currentTicker}
        noDataMessage={noDataMessage}
        showNavigator={showNavigator}
        chartHeight={chartHeight}
        currency={currency}
        tooltipSubtitleMap={{
          recommender1: recommendedItems?.recommender1?.name,
          recommender2: recommendedItems?.recommender2?.name,
          recommender3: recommendedItems?.recommender3?.name,
        }}
      />
      {(isChartHovering || lineDrawingEnabled) && drawLines && (
        <ChartActionsContainer>
          <DrawLinesButton
            variant="tertiary"
            startIcon={<GestureIcon />}
            size="small"
            onClick={() => setLineDrawingEnabled(!lineDrawingEnabled)}
          >
            {lineDrawingEnabled ? "Clear" : "Draw"}
          </DrawLinesButton>
        </ChartActionsContainer>
      )}
    </ChartLayout>
  );
};
